import React, { useState } from "react";
import axiosLib from "axios";
import dayjs from "dayjs";
import { TradeItem, TradeListError } from "@/components";
import { getCurrencySymbol, useProfile } from "@moonscape/shared";
import { Lab, Ring } from "@/components/ms";
import { LedgerTable, MonthRow } from "./ledgerTable";
import { TRADE_COLS } from "./TradeRow";

export const axios = axiosLib.create({
  baseURL: process.env.REACT_APP_SERVER_URL,
});

/**
 * Group the page's rows into the month bands the mock separates with a 2px ink
 * rule. Consecutive-only, so the order the API returned is never disturbed.
 */
const groupByMonth = (items: any[]) => {
  const groups: Array<{ key: string; label: string; total: number; rows: any[] }> = [];
  items.forEach((txn) => {
    const when = txn?.datetime || txn?.timestamp;
    const key = when ? dayjs(when).format("YYYY-MM") : "unknown";
    const last = groups[groups.length - 1];
    const value = Number(txn?.final_value ?? txn?.value ?? 0);
    if (last && last.key === key) {
      last.rows.push(txn);
      last.total += Number.isFinite(value) ? value : 0;
    } else {
      groups.push({
        key,
        label: when ? dayjs(when).format("MMMM YYYY") : "Undated",
        total: Number.isFinite(value) ? value : 0,
        rows: [txn],
      });
    }
  });
  return groups;
};

/** props comes from RecentActivity where it does a React.cloneElement and passes trades down from TradesScreen */
export const TradeList = ({
  exchange,
  asset,
  isSearching,
  trades,
  isQueryLoading,
  hasLinkedExchangeAccount,
  error,
  syncStatus,
  isPolling,
}) => {
  const [isError, setIsError] = useState(false);
  const { profile } = useProfile();
  const symbol = getCurrencySymbol(profile?.baseCurrency || "USD");

  const tradeListErrorProps = () => {
    const items = isQueryLoading ? undefined : trades?.items;
    return { error, isSearching, setIsError, trades, items };
  };

  // Check if sync is in progress
  const isSyncing = syncStatus?.state === 'starting' || syncStatus?.state === 'in_progress' ||
                    syncStatus?.stateMachine?.state === 'starting' || syncStatus?.stateMachine?.state === 'in_progress';

  // Show sync loading state if syncing and no transactions yet (but NOT when searching)
  const showSyncLoading = isSyncing && (!trades?.items || trades?.items?.length === 0) && !isQueryLoading && !isSearching;

  // Parse progress (can be string like "100.00" or number)
  const progressValue = syncStatus?.overall_progress
    ? (typeof syncStatus.overall_progress === 'string'
        ? parseFloat(syncStatus.overall_progress)
        : syncStatus.overall_progress)
    : 0;

  if (showSyncLoading) {
    return (
      <div className="mt-4 flex flex-col">
        <TradeListError {...tradeListErrorProps} />
        <div className="flex flex-col items-center justify-center gap-4 border-t border-ms-line py-16">
          <Ring pct={progressValue} label={`${Math.round(progressValue)}%`} />
          <p className="text-[15px] font-medium text-ms-ink">Syncing transactions…</p>
          {syncStatus?.current_step && (
            <Lab className="text-ms-dim">{syncStatus.current_step}</Lab>
          )}
        </div>
      </div>
    );
  }

  const items: any[] = !isQueryLoading ? trades?.items ?? [] : [];
  const groups = groupByMonth(items);

  return (
    <div className="mt-4 flex flex-col">
      <TradeListError {...tradeListErrorProps} />
      <div className="ms900:overflow-x-auto">
        <LedgerTable cols={TRADE_COLS}>
          {isQueryLoading
            ? Array(10)
                .fill(0)
                .map((txn, idx) => (
                  <TradeItem key={idx} screen="md" isQueryLoading={isQueryLoading} />
                ))
            : items.length > 0
            ? groups.map((group) => (
                <React.Fragment key={group.key}>
                  <MonthRow span={TRADE_COLS.length}>
                    {group.label} · {group.rows.length}{" "}
                    {group.rows.length === 1 ? "transaction" : "transactions"}
                    {group.total > 0 && ` · ${symbol}${Math.round(group.total).toLocaleString()}`}
                  </MonthRow>
                  {group.rows.map((txn: any) => (
                    <TradeItem
                      key={txn.id ?? txn.ext_txn_id}
                      screen="md"
                      isQueryLoading={isQueryLoading}
                      {...txn}
                    />
                  ))}
                </React.Fragment>
              ))
            : (
              <TradeItem
                screen="md"
                isEmpty={true}
                text={isSearching ? "No results found" : "No transactions"}
              />
            )}
        </LedgerTable>
      </div>
    </div>
  );
};
